import React, { useState, useEffect, useRef } from 'react';
import Modal from 'aldoo-ra/Modal';
import { Icons } from '../Icon.jsx';
import { GetCMSContent, SaveCMSContent } from 'aldoo-ra/CMS/api';
import { generateId } from '../../utils.js';
import { showPromptDialog } from 'aldoo-ra/PromptDialog';
import { showNotification } from 'aldoo-ra/Notification';
import { VariantReader } from 'aldoo-ra/VariantReader';
import { cleanDuplicateClasses } from '../style-plugins/index.js';
import { TailwindParser } from '../../utils/TailwindParser.js';

import VariantPreview from './components/VariantPreview.jsx';
import VariantPropertiesPanel from './components/VariantPropertiesPanel.jsx';
import VariantListPanel from './components/VariantListPanel.jsx';

const VariantEditorDialog = ({ isOpen, onClose, componentType = 'Text', onApply, initialKey, previewNode, activeBreakpoint: initialBreakpoint }) => {
    const targetComponent = componentType === 'Text' ? 'Typography' : componentType;
    const [currentState, setCurrentState] = useState('normal');
    const [currentBreakpoint, setCurrentBreakpoint] = useState(initialBreakpoint || 'default');

    // Sync if prop changes externally
    useEffect(() => {
        if (initialBreakpoint) setCurrentBreakpoint(initialBreakpoint);
    }, [initialBreakpoint]);

    const [variants, setVariants] = useState([]);
    const [selectedVariantId, setSelectedVariantId] = useState(null);
    const [activeKey, setActiveKey] = useState(null);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [disabledPreviewKeys, setDisabledPreviewKeys] = useState([]);
    const lastSavedVariantRef = useRef(null);

    // Layout State
    const [layout, setLayout] = useState({ leftWidth: 66.66, topHeight: 50, collectionsWidth: 50 });
    const containerRef = useRef(null);
    const leftColRef = useRef(null);
    const bottomRowRef = useRef(null);
    const isDraggingRef = useRef(false);

    const handleResizeStart = (e, type) => {
        e.preventDefault();
        isDraggingRef.current = true;
        const startX = e.clientX;
        const startY = e.clientY;
        const startLayout = { ...layout };

        const containerRect = containerRef.current?.getBoundingClientRect();
        const leftColRect = leftColRef.current?.getBoundingClientRect();
        const bottomRowRect = bottomRowRef.current?.getBoundingClientRect();

        const onMove = (moveEvent) => {
            if (type === 'leftWidth' && containerRect) {
                const deltaX = moveEvent.clientX - startX;
                const newPercent = Math.min(80, Math.max(20, startLayout.leftWidth + (deltaX / containerRect.width) * 100));
                setLayout(prev => ({ ...prev, leftWidth: newPercent }));
            } else if (type === 'topHeight' && leftColRect) {
                const deltaY = moveEvent.clientY - startY;
                const newPercent = Math.min(80, Math.max(20, startLayout.topHeight + (deltaY / leftColRect.height) * 100));
                setLayout(prev => ({ ...prev, topHeight: newPercent }));
            } else if (type === 'collectionsWidth' && bottomRowRect) {
                const deltaX = moveEvent.clientX - startX;
                const newPercent = Math.min(80, Math.max(20, startLayout.collectionsWidth + (deltaX / bottomRowRect.width) * 100));
                setLayout(prev => ({ ...prev, collectionsWidth: newPercent }));
            }
        };

        const onUp = () => {
            isDraggingRef.current = false;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        document.body.style.cursor = type === 'topHeight' ? 'row-resize' : 'col-resize';
        document.body.style.userSelect = 'none';
    };

    // Load Variants
    useEffect(() => {
        if (isOpen) {
            loadVariants();
            setActiveKey(null);
            setDisabledPreviewKeys([]);
        }
    }, [isOpen, componentType]);

    const loadVariants = async () => {
        setLoading(true);
        try {
            const res = await GetCMSContent({
                match: {
                    'content.type': 'StyleVariant',
                    'content.component': { $in: [componentType, 'Typography'] }
                },
                itemsPerPage: 1000
            });
            if (res && res.data && Array.isArray(res.data)) {
                 const flatVariants = res.data.map(item => ({
                     ...item.content,
                     _id: item._id,
                     styles: Object.entries(item.content.styles || {}).map(([key, value]) => {
                         // Clean duplicate classes when loading from CMS
                         const cleanedValue = cleanDuplicateClasses(value);
                         return { key, value: cleanedValue };
                     })
                 }));
                 // No need for strict filter here as API handles it, but keeping it is safe
                 const filtered = flatVariants;
                 setVariants(filtered);

                 if (initialKey) {
                     const found = filtered.find(v => v.styles.some(s => s.key === initialKey));
                     if (found) {
                         setSelectedVariantId(found._id);
                         setActiveKey(initialKey);
                     }
                 }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateVariant = () => {
        let name = "";
        showPromptDialog({
            title: "Create Variant Collection",
            message: (
                <div className="flex flex-col gap-2">
                    <span className="text-sm text-gray-600">Enter a name for the new style variant collection:</span>
                    <input
                        autoFocus
                        className="w-full border p-2 rounded text-sm outline-none focus:border-blue-500"
                        placeholder="e.g. My Project Styles"
                        onChange={(e) => name = e.target.value}
                    />
                </div>
            ),
            confirmText: "Create",
            onConfirm: async () => {
                if (!name) return;

                const newVariantContent = {
                    type: 'StyleVariant',
                    component: componentType === 'Text' ? 'Typography' : componentType,
                    name: name,
                    styles: {} // Save as object
                };

                const res = await SaveCMSContent({ payload: { content: newVariantContent } });
                if (res && res.result) {
                    // Add to state with array styles
                    // Handle both nested content (standard) and flattened result (if API varies)
                    const content = res.result.content || res.result;

                    const newVariantState = {
                        ...content,
                        _id: res.result._id || content._id,
                        styles: []
                    };
                    setVariants(prev => [...prev, newVariantState]);
                    setSelectedVariantId(newVariantState._id);
                }
            }
        });
    };

    const handleUpdateVariant = (updatedVariant) => {
        // Local state update only - use functional update to ensure latest state
        setVariants(prev => prev.map(v => v._id === updatedVariant._id ? updatedVariant : v));

        // Mark that we have unsaved changes
        lastSavedVariantRef.current = null;

        // Temporarily update the VariantReader cache with the new variant to show changes immediately
        // This will be overwritten when the user saves (or discarded if they cancel)
        const stylesObject = (updatedVariant.styles || []).reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {});
        VariantReader.updateCache(targetComponent, stylesObject);
        VariantReader.syncToWindow(); // Sync to live preview
        VariantReader.refresh(targetComponent);
    };

    const handleSave = async () => {
        if (!selectedVariantId) return false;
        const selectedVariant = variants.find(v => v._id === selectedVariantId);
        if (!selectedVariant) return false;

        setIsSaving(true);
        try {
            const { _id, ...content } = selectedVariant;

            // Convert styles array back to object
            // Removed cleanDuplicateClasses to prevent data loss on responsive prefixes
            const stylesObject = (content.styles || []).reduce((acc, curr) => {
                return { ...acc, [curr.key]: curr.value };
            }, {});

            const payloadContent = { ...content, styles: stylesObject };

            const res = await SaveCMSContent({ payload: { _id, content: payloadContent } });
            if (res.result) {
                showNotification({ message: 'Changes saved successfully', className: 'bg-admin_primary text-admin_text_inv' });
                // Mark that changes are saved
                lastSavedVariantRef.current = selectedVariant._id;
                return true;
            } else {
                showNotification({ message: 'Failed to save changes', className: 'bg-admin_error text-white' });
                return false;
            }
        } catch (e) {
            console.error(e);
            showNotification({ message: 'Error saving changes', className: 'bg-admin_error text-white' });
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleApply = async () => {
        if (!activeKey) return;

        if (selectedVariantId) {
            const success = await handleSave();
            if (!success) return;

            // Update cache with the data we have in state (which matches the preview)
            const selectedVariant = variants.find(v => v._id === selectedVariantId);
            if (selectedVariant) {
                const stylesObject = (selectedVariant.styles || []).reduce((acc, curr) => {
                    // Use the value directly from state (already generated by TailwindParser)
                    return { ...acc, [curr.key]: curr.value };
                }, {});
                VariantReader.updateCache(targetComponent, stylesObject);
                VariantReader.syncToWindow(); // Sync to live preview
                VariantReader.refresh(targetComponent);
            }
        }

        if (onApply) {
            const selectedVariant = variants.find(v => v._id === selectedVariantId);
            const actualComponentType = selectedVariant ? selectedVariant.component : targetComponent;
            onApply([activeKey], actualComponentType);
        }

        // Close the dialog after applying
        onClose();
    };

    const handleClose = () => {
        // Only reload from source if there are unsaved changes
        // If we just saved, the cache already has the correct data
        if (lastSavedVariantRef.current !== selectedVariantId) {
            // Reload from source to discard any unsaved temporary changes
            VariantReader.refreshFromSource(targetComponent);
        }
        // Reset the ref for next time
        lastSavedVariantRef.current = null;
        onClose();
    };

    const selectedVariant = variants.find(v => v._id === selectedVariantId);

    const addKey = () => {
        if (!selectedVariant) return;
        let key = "";

        showPromptDialog({
            title: "New Variant Key",
            message: (
                <div className="flex flex-col gap-2">
                    <span className="text-sm text-gray-600">Enter a unique key for this variant (e.g. 'title_large'):</span>
                    <input
                        autoFocus
                        className="w-full border p-2 rounded text-sm outline-none focus:border-blue-500"
                        placeholder="Variant Key"
                        onChange={(e) => key = e.target.value}
                    />
                </div>
            ),
            confirmText: "Add Key",
            onConfirm: () => {
                if (!key) return;
                // Clean key (alphanumeric + underscore/dash)
                key = key.trim().replace(/[^a-zA-Z0-9_-]/g, '_');

                if ((selectedVariant.styles || []).some(s => s.key === key)) {
                    alert("Key already exists!");
                    return;
                }

                const updated = {
                    ...selectedVariant,
                    styles: [...(selectedVariant.styles || []), { key, value: '' }]
                };
                handleUpdateVariant(updated);
                setActiveKey(key);
            }
        });
    };

    const updateKey = (keyName, newValue) => {
        if (!selectedVariant) return;
        const updated = {
            ...selectedVariant,
            styles: selectedVariant.styles.map(s => s.key === keyName ? { ...s, value: newValue } : s)
        };
        handleUpdateVariant(updated);
    };

    const removeKey = (keyName) => {
        if (!selectedVariant) {
            console.error("removeKey: No selected variant");
            return;
        }
        
        console.log("removeKey called for:", keyName);
        const targetId = selectedVariant._id;

        showPromptDialog({
            title: "Delete Variant Key",
            message: `Are you sure you want to delete '${keyName}'? This action cannot be undone.`,
            confirmText: "Delete",
            onConfirm: () => {
                console.log("removeKey confirmed");
                setVariants(prev => {
                    const currentVariant = prev.find(v => v._id === targetId);
                    if (!currentVariant) return prev;

                    const updated = {
                        ...currentVariant,
                        styles: (currentVariant.styles || []).filter(s => s.key !== keyName)
                    };
                    
                    return prev.map(v => v._id === targetId ? updated : v);
                });

                const currentStyles = selectedVariant.styles || [];
                const updatedStyles = currentStyles.filter(s => s.key !== keyName);
                
                const stylesObject = updatedStyles.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {});
                VariantReader.updateCache(targetComponent, stylesObject);
                VariantReader.refresh(targetComponent);
                
                lastSavedVariantRef.current = null;

                if (activeKey === keyName) setActiveKey(null);
            }
        });
    };

    const renameKey = (oldKeyName) => {
        if (!selectedVariant) return;
        let newKey = "";

        showPromptDialog({
            title: "Rename Variant Key",
            message: (
                <div className="flex flex-col gap-2">
                    <span className="text-sm text-gray-600">Rename variant key '{oldKeyName}':</span>
                    <input
                        autoFocus
                        defaultValue={oldKeyName}
                        className="w-full border p-2 rounded text-sm outline-none focus:border-blue-500"
                        placeholder="Variant Key"
                        onChange={(e) => newKey = e.target.value}
                    />
                </div>
            ),
            confirmText: "Rename",
            onConfirm: () => {
                if (!newKey || newKey === oldKeyName) return;

                // Clean key (alphanumeric + underscore/dash)
                newKey = newKey.trim().replace(/[^a-zA-Z0-9_-]/g, '_');

                if ((selectedVariant.styles || []).some(s => s.key === newKey)) {
                    alert("Key already exists!");
                    return;
                }

                const updated = {
                    ...selectedVariant,
                    styles: selectedVariant.styles.map(s =>
                        s.key === oldKeyName ? { ...s, key: newKey } : s
                    )
                };
                handleUpdateVariant(updated);

                // Update active key if this was the active one
                if (activeKey === oldKeyName) setActiveKey(newKey);
            }
        });
    };

    const toggleVariant = (key) => {
        setDisabledPreviewKeys(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const footer = (
        <div className="p-4 border-t border-slate-200 bg-white flex justify-end gap-2">
            <button
                onClick={onClose}
                className="px-4 py-2 text-xs font-bold text-gray-500 hover:text-gray-700"
            >
                Cancel
            </button>
            <button
                onClick={handleSave}
                disabled={isSaving || !selectedVariant}
                className="px-4 py-2 text-xs font-bold bg-white border border-slate-300 text-slate-700 rounded hover:bg-slate-50 disabled:opacity-50"
            >
                {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
                onClick={handleApply}
                disabled={!activeKey}
                className="px-4 py-2 text-xs font-bold bg-blue-600 text-white rounded hover:bg-blue-50 disabled:opacity-50 disabled:bg-slate-300"
            >
                Apply
            </button>
        </div>
    );

    // State to track which breakpoints have overrides (lifted from panel via callback)
    const [overridesMap, setOverridesMap] = useState({});

    // Copy mode state
    const [copyMode, setCopyMode] = useState(false);
    const [sourceBreakpoint, setSourceBreakpoint] = useState(null);
    const breakpointContainerRef = useRef(null);

    const handleCopyClick = () => {
        setCopyMode(true);
        setSourceBreakpoint(currentBreakpoint);
    };

    // Handle click outside to cancel copy mode
    useEffect(() => {
        if (!copyMode) return;

        const handleClickOutside = (e) => {
            if (breakpointContainerRef.current && !breakpointContainerRef.current.contains(e.target)) {
                setCopyMode(false);
                setSourceBreakpoint(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [copyMode]);

    const [breakpointCopyRequest, setBreakpointCopyRequest] = useState(null);

    const handleBreakpointClick = (bp) => {
        if (copyMode) {
            if (bp === sourceBreakpoint) {
                setCopyMode(false);
                setSourceBreakpoint(null);
                return;
            }

            // Request copy from source to target
            setBreakpointCopyRequest({ from: sourceBreakpoint, to: bp });
            setCopyMode(false);
            setSourceBreakpoint(null);
            setCurrentBreakpoint(bp);
        } else {
            setCurrentBreakpoint(bp);
        }
    };

    const headerContent = (
        <div className="flex items-center justify-between w-full pr-24">
            <span>Style Variant Browser</span>
            <div ref={breakpointContainerRef} className="flex items-center gap-2">
                <button
                    onClick={handleCopyClick}
                    className="p-1 rounded hover:bg-slate-200 text-gray-600 hover:text-gray-800 transition-colors"
                    title="Copy breakpoint properties"
                >
                    <Icons.Copy className="w-3.5 h-3.5" />
                </button>
                <div className="flex items-center bg-slate-100 rounded p-0.5 border border-slate-200 h-6">
                     {['default', 'xs', 'sm', 'md', 'lg', 'xl', '2xl'].map(bp => {
                         const hasOverrides = overridesMap[bp];
                         const isSource = copyMode && bp === sourceBreakpoint;
                         const isSelectable = copyMode && bp !== sourceBreakpoint;

                         return (
                             <button
                                key={bp}
                                onClick={() => handleBreakpointClick(bp)}
                                className={`px-2 h-full text-[10px] uppercase font-bold rounded transition-all flex items-center gap-1 ${
                                    isSource
                                        ? 'bg-blue-500 text-white shadow-sm'
                                        : isSelectable
                                        ? 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer animate-pulse'
                                        : currentBreakpoint === bp
                                        ? 'bg-white shadow-sm text-blue-600 border border-blue-100'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                                title={
                                    isSource
                                        ? 'Source (click to cancel)'
                                        : isSelectable
                                        ? `Paste to ${bp}`
                                        : `Edit ${bp} breakpoint`
                                }
                             >
                                {hasOverrides && !isSource && !isSelectable && <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />}
                                {bp === 'default' ? 'Base' : bp}
                             </button>
                         );
                     })}
                </div>
            </div>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={headerContent}
            className="w-[95vw] h-[90vh] max-w-7xl"
            closeOnClickOutside={false}
            footerContent={footer}
            styleOverrides={{ content: 'h-full p-0 flex flex-col overflow-hidden' }}
        >
            <div className="flex flex-1 min-h-0 border-t border-slate-200" ref={containerRef}>
                {/* Left Main Area (Resizable) */}
                <div
                    ref={leftColRef}
                    className="flex flex-col border-r border-slate-200 min-h-0 bg-white"
                    style={{ width: `${layout.leftWidth}%` }}
                >
                    {/* Top: Preview (Resizable Height) */}
                    <div
                        className="border-b border-slate-200 flex flex-col min-h-0"
                        style={{ height: `${layout.topHeight}%` }}
                    >
                        <div className="p-2 border-b border-slate-100 bg-slate-50 text-[10px] text-gray-500 uppercase font-bold tracking-wider">Preview</div>
                        {selectedVariant && activeKey ? (
                            <div className="flex-1 p-8 flex items-center justify-center bg-gray-100 overflow-y-auto relative" key={`preview-${activeKey}-${selectedVariant?.styles?.find(s => s.key === activeKey)?.value || ''}`}>
                                <div className="text-center w-full">
                                    <VariantPreview 
                                        variant={selectedVariant}
                                        activeKey={activeKey}
                                        disabledPreviewKeys={disabledPreviewKeys}
                                        componentType={componentType}
                                        previewNode={previewNode}
                                        forcedState={currentState}
                                        activeBreakpoint={currentBreakpoint}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 flex items-center justify-center bg-gray-50 text-gray-400 text-xs italic">
                                {selectedVariant ? "Select a key to preview" : "Select a collection to start"}
                            </div>
                        )}
                    </div>

                    {/* Resizer Horizontal */}
                    <div
                        className="h-1 bg-slate-200 hover:bg-blue-400 cursor-row-resize flex-shrink-0 z-10"
                        onMouseDown={(e) => handleResizeStart(e, 'topHeight')}
                    />

                    {/* Bottom: Collections + Keys (Row) */}
                    <VariantListPanel
                        layout={layout}
                        handleResizeStart={handleResizeStart}
                        loading={loading}
                        variants={variants}
                        search={search}
                        setSearch={setSearch}
                        handleCreateVariant={handleCreateVariant}
                        selectedVariantId={selectedVariantId}
                        setSelectedVariantId={setSelectedVariantId}
                        bottomRowRef={bottomRowRef}
                        selectedVariant={selectedVariant}
                        addKey={addKey}
                        activeKey={activeKey}
                        setActiveKey={setActiveKey}
                        disabledPreviewKeys={disabledPreviewKeys}
                        toggleVariant={toggleVariant}
                        removeKey={removeKey}
                        renameKey={renameKey}
                    />
                </div>

                {/* Resizer Vertical (Left vs Right) */}
                <div
                    className="w-1 bg-slate-200 hover:bg-blue-400 cursor-col-resize flex-shrink-0 z-10"
                    onMouseDown={(e) => handleResizeStart(e, 'leftWidth')}
                />

                {/* Right Area (1/3 width): Properties Panel */}
                <div className="flex-1 flex flex-col bg-slate-50 min-h-0 border-l border-slate-200 min-w-[480px]">
                    <VariantPropertiesPanel
                        key={activeKey}
                        activeKey={activeKey}
                        classString={selectedVariant && activeKey ? (selectedVariant.styles || []).find(s => s.key === activeKey)?.value || '' : ''}
                        onChange={(val) => updateKey(activeKey, val)}
                        onToggle={toggleVariant}
                        currentState={currentState}
                        onStateChange={setCurrentState}
                        activeBreakpoint={currentBreakpoint}
                        onBreakpointChange={setCurrentBreakpoint}
                        onOverridesUpdate={setOverridesMap}
                        breakpointCopyRequest={breakpointCopyRequest}
                        onBreakpointCopyComplete={() => setBreakpointCopyRequest(null)}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default VariantEditorDialog;
